import type { ReactNode } from "react";

import { PAGE_SIZES } from "@/lib/office/pagination";

import { PageSizeSelect } from "../../PageSizeSelect";
import { ActionLink } from "./PageNavigation";

/** Listing-pager — mirrors the public-site catalog pagination: prev /
 *  current-of-total / next, each prev/next wrapped in the corner-mark
 *  affordance (greyed out when at the boundary). `buildHref(p)` returns
 *  the URL for page `p`, so each page owns its own query-string shape. */
export function Pagination({
  page,
  totalPages,
  buildHref,
  trailing,
  className = "mt-6",
}: {
  page: number;
  totalPages: number;
  buildHref: (page: number) => string;
  /** Optional right-aligned content (e.g. "N total" counter, a page-size selector). */
  trailing?: ReactNode;
  /** Margin wrapper override. Defaults to `mt-6` (under the table); pass
   *  `mb-6` when the toolbar sits above the table. */
  className?: string;
}) {
  if (totalPages <= 1 && !trailing) return null;
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-4 font-mono text-xs ${className}`}
    >
      <nav aria-label="Pagination" className="flex items-center gap-3">
        {page > 1 ? (
          <ActionLink href={buildHref(page - 1)}>
            <span aria-label="Previous page">
              ←<span className="hidden sm:inline"> prev</span>
            </span>
          </ActionLink>
        ) : (
          <ActionLink>
            <span aria-label="Previous page">
              ←<span className="hidden sm:inline"> prev</span>
            </span>
          </ActionLink>
        )}
        <span className="text-fg-muted">
          {String(page).padStart(2, "0")}
          <span className="px-1 text-fg-subtle">
            <span className="hidden sm:inline">of</span>
            <span className="sm:hidden">/</span>
          </span>
          {String(totalPages).padStart(2, "0")}
        </span>
        {page < totalPages ? (
          <ActionLink href={buildHref(page + 1)}>
            <span aria-label="Next page">
              <span className="hidden sm:inline">next </span>→
            </span>
          </ActionLink>
        ) : (
          <ActionLink>
            <span aria-label="Next page">
              <span className="hidden sm:inline">next </span>→
            </span>
          </ActionLink>
        )}
      </nav>
      {trailing && <div className="text-fg-muted">{trailing}</div>}
    </div>
  );
}

/** The "per page N" control for a list page's {@link Pagination} `trailing`
 *  slot — the label + page-size `<select>`, identical on every list. Pass the
 *  page's current size and its default (so the param drops when unchanged). */
export function PerPageSelect({
  pageSize,
  defaultSize,
}: {
  pageSize: number;
  defaultSize: number;
}) {
  return (
    <label className="flex items-center gap-2 text-fg-muted">
      <span>per page</span>
      <PageSizeSelect
        value={pageSize}
        options={PAGE_SIZES}
        defaultValue={defaultSize}
        ariaLabel="Page size"
      />
    </label>
  );
}
